import { AuthUser } from 'types/entities';
import type { FoundationStatusType, FoundationType } from 'types/entities/foundation';
import { getAccessToken, setAccessToken } from './token.storage';
import { base64UrlToUtf8String, utf8StringToBase64Url } from './base64UrlUtf8';
export interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  data: AuthUser;
}

export const decodeToken = (token: string): TokenPayload | null => {
  try {
    const parts = token.split('.');

    if (parts.length !== 3) {
      console.error('Token JWT inválido: formato incorreto');
      return null;
    }

    const payload = parts[1];
    const decoded = base64UrlToUtf8String(payload);

    return JSON.parse(decoded) as TokenPayload;
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const payload = decodeToken(token);

  if (!payload || !payload.exp) {
    return true;
  }

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000;

  return currentTime >= expirationTime - bufferTime;
};

export const isTokenValid = (token: string | null): boolean => {
  if (!token) {
    return false;
  }

  if (isTokenExpired(token)) {
    return false;
  }

  return true;
};

export const getTokenPayload = (): TokenPayload | null => {
  const token = getAccessToken();

  if (!token) {
    return null;
  }

  return decodeToken(token);
};

export const getValidToken = (): string | null => {
  const token = getAccessToken();

  if (!token || !isTokenValid(token)) {
    return null;
  }

  return token;
};

export const getUserFromToken = (): AuthUser | null => {
  const payload = getTokenPayload();

  if (!payload || !payload.data) {
    return null;
  }

  const data = payload.data;
  const foundation = data?.foundation;

  return {
    id: data.id,
    name: data.name,
    email: data.email,
    foundation: {
      id: foundation.id,
      type: foundation.type as FoundationType,
      status: foundation.status as FoundationStatusType,
    },
  };
};

export const getTokenExpirationTime = (): number | null => {
  const token = getAccessToken();

  if (!token) {
    return null;
  }

  const payload = decodeToken(token);

  if (!payload || !payload.exp) {
    return null;
  }

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return Math.max(0, expirationTime - currentTime);
};
